import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../../../lib/api";
import { useLang } from "../../../context/LangContext";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import { Label } from "../../../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const LANG_NAMES = { pt: ["Português", "Portuguese"], en: ["Inglês", "English"] };

export default function DubStartDialog({ job, cfg, onClose, onStarted }) {
  const { t } = useLang();
  const detected = (job?.detected_language || "").toLowerCase();
  const [target, setTarget] = useState(detected.startsWith("pt") || detected === "portuguese" ? "en" : "pt");
  const [voice, setVoice] = useState("auto");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      await api.post(`/dub/jobs/${job.id}/start`, { target_language: target, voice, rights_accepted: accepted });
      toast.success(t("Processamento iniciado", "Processing started"));
      onStarted();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dub-start-dialog">
        <DialogHeader><DialogTitle>{t("Confirmar processamento", "Confirm processing")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm font-semibold break-words">{job?.title_translated || job?.title}</p>
          {job?.title_translated && job.title_translated !== job.title && <p className="-mt-3 text-xs text-muted-foreground break-words">{job.title}</p>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("Traduzir para", "Translate to")}</Label>
              <Select value={target} onValueChange={setTarget}><SelectTrigger data-testid="dub-target-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{(cfg?.languages || ["pt", "en"]).map((l) => <SelectItem key={l} value={l}>{t(...(LANG_NAMES[l] || [l, l]))}</SelectItem>)}</SelectContent></Select>
            </div>
            <div>
              <Label className="text-xs">{t("Voz", "Voice")}</Label>
              <Select value={voice} onValueChange={setVoice}><SelectTrigger data-testid="dub-voice-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="auto">{t("Automático (segue as falas)", "Automatic (follows speakers)")}</SelectItem><SelectItem value="m">{t("Tudo masculino", "All male")}</SelectItem><SelectItem value="f">{t("Tudo feminino", "All female")}</SelectItem></SelectContent></Select>
              {voice === "auto" && <p className="mt-1.5 text-[11px] text-muted-foreground">{t("Identifica cada orador pelo tom de voz: homens com voz masculina, mulheres com voz feminina, uma voz diferente por pessoa.", "Identifies each speaker by voice tone: men get male voices, women female voices, a different voice per person.")}</p>}
            </div>
          </div>
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200 flex gap-2" data-testid="dub-copyright-warning">
            <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{t("Aviso: só processe vídeos próprios ou para os quais tenha direitos ou autorização (formação, marketing, corporativos ou educacionais). Traduzir e dobrar conteúdos de terceiros sem autorização pode violar direitos de autor. A sua aceitação fica registada com data, hora e IP para auditoria.",
              "Notice: only process your own videos or videos you hold rights or authorisation for (training, marketing, corporate or educational). Translating and dubbing third-party content without permission may infringe copyright. Your acceptance is recorded with date, time and IP for auditing.")}</span>
          </div>
          <label className="flex items-start gap-3 text-sm cursor-pointer">
            <Checkbox data-testid="dub-rights-checkbox" checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" />
            <span>{cfg?.statement}</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("Cancelar", "Cancel")}</Button>
          <Button data-testid="dub-confirm-start-btn" disabled={!accepted || busy} onClick={start} className="gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("Confirmar e processar", "Confirm and process")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
